import { useState, useEffect, useCallback } from 'react';
import { schoolApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Loader2, ClipboardList, Search } from 'lucide-react';
import { toast } from 'sonner';

const EVAL_TYPES = [
  { value: 'bind', label: 'Bind (حفظ)' },
  { value: 'wett', label: 'Wett (ود)' },
  { value: 'nafarr', label: 'Nafarr (نفر)' },
];

const STATUS_OPTIONS = [
  { value: 'suivi', label: 'Suivi (حـ)' },
  { value: 'non_suivi', label: 'Non suivi (لم)' },
  { value: 'absent', label: 'Absent' },
  { value: 'malade', label: 'Malade' },
];

const statusColors = {
  suivi: 'bg-emerald-50 text-emerald-700',
  non_suivi: 'bg-red-50 text-red-700',
  absent: 'bg-stone-100 text-stone-600',
  malade: 'bg-amber-50 text-amber-700',
};

export default function SchoolEvaluationsPage() {
  const [evaluations, setEvaluations] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterClass, setFilterClass] = useState('all');
  const [form, setForm] = useState({
    student_id: '', class_id: '', evaluation_date: new Date().toISOString().split('T')[0],
    eval_type: 'bind', status: 'suivi', surah_id: null, start_verse: null, end_verse: null, notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      const params = { evaluation_date: filterDate };
      if (filterClass !== 'all') params.class_id = filterClass;
      const [evalsRes, classesRes, studentsRes] = await Promise.all([
        schoolApi.getEvaluations(params),
        schoolApi.getClasses(),
        schoolApi.getStudents(filterClass !== 'all' ? { class_id: filterClass } : {}),
      ]);
      setEvaluations(evalsRes.data);
      setClasses(classesRes.data);
      setStudents(studentsRes.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [filterDate, filterClass]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await schoolApi.createEvaluation(form);
      toast.success('Evaluation ajoutee');
      setDialogOpen(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const getStudentName = (id) => { const s = students.find(s => s.id === id); return s ? `${s.first_name} ${s.last_name}` : id; };

  return (
    <div className="space-y-6" data-testid="evaluations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Evaluations</h1>
          <p className="text-sm text-stone-500">{evaluations.length} evaluation(s)</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-eval-btn"><Plus className="w-4 h-4 mr-1" /> Evaluer</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle evaluation</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label>Classe</Label>
                <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                  <SelectTrigger data-testid="eval-class"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Eleve</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger data-testid="eval-student"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{students.filter(s => !form.class_id || s.class_id === form.class_id).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.eval_type} onValueChange={(v) => setForm({ ...form, eval_type: v })}>
                    <SelectTrigger data-testid="eval-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{EVAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="eval-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.evaluation_date} onChange={(e) => setForm({ ...form, evaluation_date: e.target.value })} data-testid="eval-date" />
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="eval-notes" /></div>
              <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="eval-submit">Enregistrer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-40 h-9" data-testid="filter-date" />
        <Select value={filterClass} onValueChange={setFilterClass}>
          <SelectTrigger className="w-40 h-9" data-testid="filter-eval-class"><SelectValue placeholder="Toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les classes</SelectItem>
            {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
      ) : evaluations.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-stone-400">Aucune evaluation pour cette date</CardContent></Card>
      ) : (
        <Card className="border-stone-200">
          <div className="divide-y divide-stone-100">
            {evaluations.map(ev => (
              <div key={ev.id} className="p-3 flex items-center justify-between hover:bg-stone-50" data-testid={`eval-row-${ev.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><ClipboardList className="w-4 h-4 text-amber-600" /></div>
                  <div>
                    <p className="text-sm font-medium text-stone-900">{getStudentName(ev.student_id)}</p>
                    <p className="text-xs text-stone-400">{EVAL_TYPES.find(t => t.value === ev.eval_type)?.label || ev.eval_type}</p>
                  </div>
                </div>
                <Badge className={`text-xs ${statusColors[ev.status] || 'bg-stone-100 text-stone-600'}`}>
                  {STATUS_OPTIONS.find(s => s.value === ev.status)?.label || ev.status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
